import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Phone, Mail, RefreshCcw, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["new", "contacted", "appointment_set", "offer_made", "under_contract", "follow_up_later", "dead"];

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [status, setStatus] = useState("new");
  const [adminNotes, setAdminNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [resending, setResending] = useState(false);

  const load = async () => {
    const r = await api.get(`/admin/leads/${id}`);
    const d = r.data;
    setLead(d);
    setStatus(d.status || "new");
    setAdminNotes(d.admin_notes || "");
    setInternalNotes(d.internal_notes || "");
    setFollowUp(d.follow_up_date || "");
    setAssignedTo(d.assigned_to || "");
  };
  useEffect(() => { load().catch(() => navigate("/admin/dashboard")); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    try {
      await api.patch(`/admin/leads/${id}`, {
        status, admin_notes: adminNotes, internal_notes: internalNotes,
        follow_up_date: followUp, assigned_to: assignedTo,
      });
      toast.success("Saved"); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post(`/admin/leads/${id}/retry`);
      const o = r.data?.owner_notification?.status; const p = r.data?.podio_sync?.status;
      toast[o === "sent" ? "success" : "error"](`Owner email: ${o || "n/a"} · Podio: ${p || "n/a"}`);
      load();
    }
    catch (e) { toast.error("Retry failed"); }
    setResending(false);
  };

  const copySummary = async () => {
    if (!lead) return;
    const lines = [
      `Lead: ${lead.name} (${lead.lead_type})`,
      `Priority: ${lead.lead_priority}`,
      `Phone: ${lead.phone}`,
      `Email: ${lead.email}`,
      lead.property_address ? `Property: ${lead.property_address}` : null,
      lead.timeline ? `Timeline: ${lead.timeline}` : null,
      lead.biggest_issue ? `Goal/Issue: ${lead.biggest_issue}` : null,
      `Created: ${lead.created_at}`,
    ].filter(Boolean).join("\n");
    try { await navigator.clipboard.writeText(lines); toast.success("Summary copied"); }
    catch { toast.error("Copy failed"); }
  };

  if (!lead) return <div className="min-h-screen bg-black text-neutral-500 p-10">Loading…</div>;

  const fields = Object.entries(lead).filter(([k]) =>
    !["_id", "id", "admin_notes", "internal_notes", "status", "follow_up_date", "assigned_to"].includes(k)
  );

  const pri = lead.lead_priority || "Cold";
  const priCls = pri === "Hot" ? "bg-red-500/15 text-red-300 border-red-500/30"
    : pri === "Warm" ? "bg-[#C19B6C]/15 text-[#C19B6C] border-[#C19B6C]/30"
    : "bg-white/5 text-neutral-400 border-white/10";

  return (
    <div className="min-h-screen bg-black text-neutral-200">
      <header className="border-b border-white/10 sticky top-0 bg-black/85 backdrop-blur z-30">
        <div className="max-w-5xl mx-auto px-5 py-5 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-neutral-300 hover:text-[#C19B6C]"><ArrowLeft size={14} /> Back</Link>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={copySummary} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2"><Copy size={14}/> Copy summary</button>
            <button onClick={resend} disabled={resending} className="btn-ghost px-4 py-2 text-sm inline-flex items-center gap-2"><RefreshCcw size={14}/> {resending ? "Retrying…" : "Retry email / Podio"}</button>
            <button onClick={save} className="btn-gold px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2" data-testid="lead-save"><Save size={14} /> Save</button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="overline">{lead.lead_type}</div>
          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${priCls}`}>{pri}</span>
          {lead.duplicate_flag && <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-amber-500/30 text-amber-300 bg-amber-500/10">Duplicate</span>}
          {lead.email_sent ? (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-500/30 text-emerald-300 bg-emerald-500/10" data-testid="lead-notif-sent">Owner email sent</span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-2 py-1 border border-red-500/30 text-red-300 bg-red-500/10" data-testid="lead-notif-failed">Owner email not sent</span>
          )}
        </div>
        <h1 className="font-display text-3xl mt-3">{lead.name}</h1>
        <div className="text-sm text-neutral-400 flex flex-wrap gap-4 mt-1">
          <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-1 hover:text-[#C19B6C]" data-testid="lead-call"><Phone size={12}/> {lead.phone}</a>
          <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1 hover:text-[#C19B6C]" data-testid="lead-email"><Mail size={12}/> {lead.email}</a>
        </div>
        {lead.duplicate_reason && <div className="text-xs text-amber-300 mt-2">⚠ {lead.duplicate_reason}</div>}
        {lead.owner_notification && (
          <div className="text-xs text-neutral-400 mt-2 space-y-1" data-testid="lead-notif-detail">
            <div>Owner email: {lead.owner_notification.status} · attempts {lead.owner_notification.attempts || 0}{lead.owner_notification.to?.length ? ` · to ${lead.owner_notification.to.join(", ")}` : ""}{lead.owner_notification.sent_at ? ` · accepted ${lead.owner_notification.sent_at.slice(0, 16).replace("T", " ")} UTC` : ""}</div>
            {lead.visitor_confirmation && ["seller", "investor", "buyer"].includes(lead.lead_type) && (
              <div data-testid="lead-visitor-detail">Visitor confirmation: {lead.visitor_confirmation.status} · attempts {lead.visitor_confirmation.attempts || 0} · email verification: <span className={lead.email_verification_status === "verified" ? "text-emerald-300" : "text-[#C19B6C]"}>{lead.email_verification_status || "unverified"}</span>{lead.visitor_confirmation.last_error ? ` · ${lead.visitor_confirmation.last_error}` : ""}</div>
            )}
            {lead.podio_sync && (
              <div data-testid="lead-podio-detail">Podio: {lead.podio_sync.status} · attempts {lead.podio_sync.attempts || 0}{lead.podio_sync.item_id ? <> · item <a className="text-[#C19B6C]" href={lead.podio_sync.item_url || "#"} target="_blank" rel="noreferrer">{lead.podio_sync.item_id}</a></> : ""}{lead.podio_sync.last_error ? ` · ${lead.podio_sync.last_error}` : ""}</div>
            )}
          </div>
        )}
        {lead.email_error && <div className="text-xs text-red-300 mt-2">Email error: {lead.email_error}</div>}

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card-frame p-6">
            <h3 className="font-display text-lg mb-4">Submitted Data</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {fields.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-xs uppercase tracking-wider text-neutral-500">{k}</dt>
                  <dd className="text-neutral-200 break-words">
                    {Array.isArray(v) ? (
                      v.map((u, i) => { const p = typeof u === "string" && u.includes("/api/files/") ? u.slice(u.indexOf("/api/files/")) : (typeof u === "string" && u.startsWith("/") ? u : null); return p ? (
                        <a key={i} href={p} target="_blank" rel="noreferrer" className="text-[#C19B6C] underline mr-2" data-testid={`lead-file-${i}`}>file {i+1}</a>
                      ) : <span key={i} className="mr-2">{String(u)}</span>; })
                    ) : typeof v === "string" && v.startsWith("/api/files/") ? (
                      <a href={v} target="_blank" rel="noreferrer" className="text-[#C19B6C] underline">view file</a>
                    ) : typeof v === "boolean" ? (v ? "yes" : "no") : (typeof v === "object" ? JSON.stringify(v) : String(v ?? "—"))}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="card-frame p-6 h-fit space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm" data-testid="lead-status">
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500">Follow-up date</label>
              <input type="date" value={followUp ? followUp.slice(0,10) : ""} onChange={(e) => setFollowUp(e.target.value)} className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm" data-testid="lead-followup" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500">Assigned to</label>
              <input value={assignedTo} onChange={(e) => setAssignedTo(e.target.value)} placeholder="email or name" className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm" data-testid="lead-assigned" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500">Admin notes</label>
              <textarea value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} rows={4} className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm" data-testid="lead-notes" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-neutral-500">Internal notes</label>
              <textarea value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} rows={4} className="w-full bg-[#0F0F0F] border border-white/10 px-3 py-2.5 mt-2 text-sm" data-testid="lead-internal-notes" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
